import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { identity, shuffle } from 'lodash';
import {
  CALENDAR_VIEW,
  CalendarEvent,
  CalendarFilter,
  EventsApiResponse,
  SearchResult,
  VIEWS,
} from './types';
import chroma from 'chroma-js';
import { IStrategy } from '../../../types/model';

enum COLORS {
  DARK_CYAN = '#17BAC5',
  TEAL = '#1AE6E0',
  LIGHT_BLUE = '#79CFFF',
  BLUE = '#5272E4',
  PURPLE = '#C780FF',
  ORANGE = '#FF965C',
  PINK = '#FFA3E6',
}

const EVENT_COLORS = shuffle(Object.values(COLORS));

export function getISOTime(date: Date | moment.Moment) {
  return date.toISOString().replace(/\.[0-9]{3}/, '');
}

export function getEventBackgroundColor(color: string) {
  return chroma(color).brighten().hex();
}

export function getEventUrl(url) {
  return url.includes('http') ? url : `http://${url}`;
}

export function getStartEndOf(date: Date, unitOfTime: 'isoWeek' | 'month') {
  const d = moment(date);
  return {
    startDate: getISOTime(d.startOf(unitOfTime).toDate()),
    endDate: getISOTime(d.endOf(unitOfTime).toDate()),
  };
}

const mapCodeToCategory = {
  ICC: 'Corporate',
  IEP: 'Economic and Political',
  IDC: 'Treasury Auctions',
};

export function groupEventFilters(filters: CalendarFilter[]): {} {
  const groups = {};

  filters.forEach((filter) => {
    const groupId = mapCodeToCategory[filter.code.split('_')[0]];
    groups[groupId] = groups[groupId] || [];
    groups[groupId].push(filter);
  });

  return groups;
}

export function transformFiltersToCheckbox(filters: CalendarFilter[]) {
  const groupedFilters = groupEventFilters(filters);
  const categories = Object.keys(groupedFilters);

  return categories.map((category) => {
    return {
      isChecked: false,
      title: category,
      options: groupedFilters[category].map((option) => ({
        isChecked: false,
        ...option,
      })),
    };
  });
}

export function extractStockAssetsFromStrategies(
  strategies: IStrategy[]
): SearchResult[] {
  return strategies.map((strategy) => {
    return {
      id: strategy.id,
      name: strategy.name,
      logo: strategy.color,
      symbol: undefined,
      type: 'STRATEGY',
      strategyAssets: strategy.strategyAssets.items
        .map((asset) => asset.asset)
        .filter((asset) => asset.type === 'STOCK'),
    };
  });
}

export function extractCalendarData(
  events: EventsApiResponse['getNewsCalendar']['items']
): CalendarEvent[] {
  return (
    events
      .filter(identity)
      .map(
        (
          {
            longDescription,
            dateGmt,
            description,
            eventUrl,
            organizationName,
            id,
          },
          index
        ) => {
          return {
            id: id,
            start: new Date(dateGmt),
            end: new Date(dateGmt),
            title: description,
            extendedProps: {
              eventUrl,
              organizationName,
              description,
              longDescription,
              color: EVENT_COLORS[index % (EVENT_COLORS.length - 1)],
            },
          };
        }
      ) || []
  );
}

export const useEconomicCalendar = () => {
  const [calendarView, setCalendarView] = useState<CALENDAR_VIEW>(VIEWS.week);
  const calendarRef = useRef<any>();

  useEffect(() => {
    const API = getCalendarInstance();
    API && API.changeView(calendarView);
  }, [calendarView]);

  const getCalendarInstance = () => {
    const { current: calendarDom } = calendarRef;
    const API = calendarDom ? calendarDom.getApi() : null;
    return API;
  };

  return {
    calendarView,
    setCalendarView,
    getCalendarInstance,
    calendarRef,
  };
};
